import { useState } from 'react';
import { faCheck, faXmark } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import { numeralFormatterCurrency } from 'ns_libs/formatter';
import { NSCard, NSButton } from 'bricks';
import NSCellInput from 'bricks/NSCellInput/NSCellInput';
import RenderIf from 'components/RenderIf/RenderIf';
import useToast from 'hooks/useToast';
import { CalculatedSubline, SaveInput, UsesCardEditContext, UsesCardEditVariant } from 'views/ProFormaTable/types';

import '../UsesCard/UsesCard.scss';

interface Props {
    id: string;
    title: string;
    total: number | null | undefined;
    costPerRSF: number | null | undefined;
    variant: UsesCardEditVariant;
    context?: UsesCardEditContext;
    initialMode?: 'dollar' | 'percentage';
    calculatedSublines?: CalculatedSubline[];
    isLastCard?: boolean;
    onSave: (input: SaveInput) => void;
    onCancel: () => void;
}

const UsesCardEditView = ({
    id,
    title,
    total,
    costPerRSF,
    variant,
    context,
    initialMode = 'dollar',
    calculatedSublines,
    isLastCard = false,
    onSave,
    onCancel,
}: Props) => {
    const { showError } = useToast();
    const [draftMode, setDraftMode] = useState<'dollar' | 'percentage'>(initialMode);
    const [draftValue, setDraftValue] = useState<number | null>(total ?? null);
    const [draftPercent, setDraftPercent] = useState<number | null>(context?.initialPercent ?? null);

    const hasPercentMode = variant !== 'dollar-only';
    const percentButtonLabel = variant === 'dollar-or-pct-of-debt' ? '% of loan' : '% Relative';
    const contextLabel = variant === 'dollar-or-pct-of-debt' ? 'of debt amount' : 'of hard costs';

    const contextBase = variant === 'dollar-or-pct-of-debt' ? context?.debtAmount : context?.hardTotal;
    const contextDollars = draftMode === 'percentage' && draftPercent != null && contextBase != null
        ? contextBase * draftPercent
        : null;

    const handleSave = () => {
        if (draftMode === 'dollar') {
            if (draftValue == null) return;
            onSave({ mode: 'dollar', value: draftValue });
            return;
        }

        if (draftPercent == null) return;

        const percent = draftPercent * 100;
        if (variant === 'dollar-or-pct-of-hard') {
            if (context?.hardTotal == null) {
                showError('Enter a hard cost amount before saving soft costs as a percentage.');
                return;
            }
            onSave({ mode: 'soft-pct', percent, hardTotal: context.hardTotal });
            return;
        }

        if (variant === 'dollar-or-pct-of-debt') {
            onSave({ mode: 'financing-pct', percent });
        }
    };

    return (
        <NSCard
            id={id}
            className={classNames('UsesCard NSCard--level-5 d-flex flex-column border-primary p-3', { 'mr-3': !isLastCard })}
        >
            <div className="d-flex align-items-start justify-content-between mb-1">
                <div className="d-flex flex-column flex-grow-1">
                    <div className="text-dark mb-2">{title}</div>
                    <RenderIf isTrue={hasPercentMode}>
                        <div className="d-flex mb-1 w-100" role="group" aria-label="Input mode">
                            <NSButton
                                text="$ Total"
                                ariaLabel="Dollar input mode"
                                ariaPressed={draftMode === 'dollar'}
                                allowReadOnlyAccess
                                className={classNames('font-12 font-weight-bold text-nowrap text-dark w-100', {
                                    'bg-primary border-primary': draftMode === 'dollar',
                                    'bg-transparent': draftMode !== 'dollar',
                                })}
                                callback={() => setDraftMode('dollar')}
                            />
                            <NSButton
                                text={percentButtonLabel}
                                ariaLabel="Percentage input mode"
                                ariaPressed={draftMode === 'percentage'}
                                allowReadOnlyAccess
                                className={classNames('font-12 font-weight-bold text-nowrap text-dark w-100', {
                                    'bg-primary border-primary': draftMode === 'percentage',
                                    'bg-transparent': draftMode !== 'percentage',
                                })}
                                callback={() => setDraftMode('percentage')}
                            />
                        </div>
                    </RenderIf>
                    <div className="UsesCard__hero UsesCardEditView__hero">
                        <NSCellInput
                            type="number"
                            numberType={draftMode === 'dollar' ? 'currency' : 'percentage'}
                            inputElementClassName="font-24 font-weight-semibold"
                            labelClassName="font-24 font-weight-semibold"
                            value={draftMode === 'dollar' ? draftValue : draftPercent}
                            handleUpdateValue={updatedValue => {
                                const numeric = Number(updatedValue);
                                const safeValue = Number.isFinite(numeric) ? numeric : null;
                                if (draftMode === 'dollar') {
                                    setDraftValue(safeValue);
                                } else {
                                    setDraftPercent(safeValue);
                                }
                            }}
                        />
                    </div>
                </div>
                <div className="d-flex">
                    <NSButton
                        icon={faXmark}
                        size="sm"
                        ariaLabel={`Cancel editing ${title}`}
                        className="p-1"
                        callback={onCancel}
                    />
                    <NSButton
                        icon={faCheck}
                        size="sm"
                        ariaLabel={`Save ${title}`}
                        className="p-1 text-primary"
                        callback={handleSave}
                    />
                </div>
            </div>
            <RenderIf isTrue={draftMode === 'percentage'}>
                <div className="font-12">
                    <span>
                        {contextLabel}
                        {contextDollars != null ? ` · ${numeralFormatterCurrency(contextDollars, '$0,0')}` : ''}
                    </span>
                </div>
            </RenderIf>
            <div className="UsesCard__subtitle font-12">
                {costPerRSF != null && !Number.isNaN(costPerRSF) ? `${numeralFormatterCurrency(costPerRSF)} / RSF` : '—'}
            </div>
            {calculatedSublines?.map(({ label, amount }) => (
                amount != null && (
                    <div key={label} className="UsesCard__subtitle font-12">
                        {`${numeralFormatterCurrency(amount, '$0,0')} ${label}`}
                    </div>
                )
            ))}
        </NSCard>
    );
};

export default UsesCardEditView;
